'use client';

import { Card, Label, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import type { SumSquaredErrorsResult } from '../../../lib/stats/sum-squared-errors';

interface SumSquaredErrorsCalculatorProps {
  visualizationData: SumSquaredErrorsResult | null;
}

export function SumSquaredErrorsCalculator({ visualizationData: result }: SumSquaredErrorsCalculatorProps) {
  if (!result) {
    return null;
  }

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Sum of Squared Errors Results</h3>
          <div className="space-y-4">
            <div>
              <Label>Mean</Label>
              <div className="text-lg font-semibold">{result.mean !== undefined ? result.mean.toFixed(4) : 'N/A'}</div>
            </div>
            <div>
              <Label>Sum of Squared Errors (SSE)</Label>
              <div className="text-lg font-semibold">{result.sse !== undefined ? result.sse.toFixed(4) : 'N/A'}</div>
              <div className="text-sm text-muted-foreground mt-1">{result.interpretation}</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Plot</h3>
          <DistributionGraph
            data={result.data || []}
            height={300}
            showMean
            grid={{
              left: '10%',
              right: '5%',
              top: '10%',
              bottom: '10%'
            }}
          />
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Calculation Details</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Value</TableHead>
                <TableHead>Deviation from Mean</TableHead>
                <TableHead>Squared Deviation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.values && result.values.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.value !== undefined ? item.value.toFixed(2) : 'N/A'}</TableCell>
                  <TableCell>{item.deviation !== undefined ? item.deviation.toFixed(4) : 'N/A'}</TableCell>
                  <TableCell>{item.squaredDeviation !== undefined ? item.squaredDeviation.toFixed(4) : 'N/A'}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell></TableCell>
                <TableCell>Sum of Squared Errors:</TableCell>
                <TableCell>{result.sse !== undefined ? result.sse.toFixed(4) : 'N/A'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      </div>
    </ScrollableContainer>
  );
} 